import { useEffect, useState } from 'react';

/**
 * Deployment branding, read at run time from `branding.json` next to the app (served from the app's base path), so an
 * operator changes it without a rebuild, and overridden by what head office set (ADR 0106) when the app registered its
 * server with {@link setBrandingSource}. Missing, unreadable or empty means no "Powered by" note.
 */
export interface Branding {
  productName?: string;
  /** Who holds the copyright shown in footers; falls back to the "Powered by" name, then the product name. */
  copyright?: string;
  poweredBy?: { name?: string; /** http(s) address the note links to; empty for plain text. */ url?: string };
  /** The brand's logo as the server keeps it (the store's copy, ADR 0106); without it, the app's own `logo.png`. */
  logoUrl?: string;
}

/** What the app's server answers (`GET /api/v1/identity/branding`): null where head office set nothing. */
export interface ServerBranding {
  productName?: string | null;
  copyright?: string | null;
  poweredBy?: { name: string; url?: string | null } | null;
  logoUrl?: string | null;
}

let loading: Promise<Branding> | undefined;
let source: (() => Promise<ServerBranding | undefined>) | undefined;

/**
 * Where head office's branding comes from: the app's own server, through its API client (the store API for the POS, KDS and
 * store admin; the HO admin API for HO admin). Set once at start, before the first screen.
 */
export function setBrandingSource(load: (() => Promise<ServerBranding | undefined>) | undefined) {
  source = load;
  loading = undefined;
}

/** Head office's values over the deployment's, field by field. */
export function mergeBranding(file: Branding, server: ServerBranding | undefined): Branding {
  if (!server) return file;
  const merged: Branding = { ...file };
  if (server.productName) merged.productName = server.productName;
  if (server.copyright) merged.copyright = server.copyright;
  if (server.poweredBy?.name) merged.poweredBy = { name: server.poweredBy.name, url: server.poweredBy.url ?? '' };
  if (server.logoUrl) merged.logoUrl = server.logoUrl;
  return merged;
}

/** Fetched once per page load; a failure is not remembered, so the next screen tries again. */
export function loadBranding(): Promise<Branding> {
  loading ??= Promise.all([
    // A static file of the app, not an API call, so not through @pos/api-client (same as the POS /health check).
    globalThis
      .fetch(`${import.meta.env.BASE_URL}branding.json`, { cache: 'no-cache' })
      .then((response) => (response.ok ? (response.json() as Promise<Branding>) : {}))
      .catch((): Branding => ({})),
    // The server may be unreachable or not paired yet: the deployment's own branding then stands.
    source ? source().catch(() => undefined) : Promise.resolve(undefined),
  ])
    .then(([file, server]) => mergeBranding(file, server))
    .then((branding) => {
      if (Object.keys(branding).length === 0) loading = undefined;
      return branding;
    });
  return loading;
}

/** Forgets what was fetched (tests, or after an operator edits branding.json without reloading). */
export function resetBranding() {
  loading = undefined;
}

export function useBranding(): Branding | undefined {
  const [branding, setBranding] = useState<Branding>();
  useEffect(() => {
    let current = true;
    void loadBranding().then((value) => current && setBranding(value));
    return () => {
      current = false;
    };
  }, []);
  return branding;
}

/**
 * The logo for a sign-in screen: the brand's from head office (ADR 0106), else the app's own `logo.png`, each tried in turn when
 * it fails to load. Undefined while branding loads, so one logo never flashes before the other, and when neither loads.
 */
export function useBrandLogo(): { src: string; onError(): void } | undefined {
  const branding = useBranding();
  const [failed, setFailed] = useState<readonly string[]>([]);
  if (!branding) return undefined;
  const src = [branding.logoUrl, `${import.meta.env.BASE_URL}logo.png`].find((url): url is string => !!url && !failed.includes(url));
  return src ? { src, onError: () => setFailed((list) => [...list, src]) } : undefined;
}
